import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    AnankeClockIcon,
    AnankeLinkExternalIcon,
    AnankeModelVariantIcon,
    AnankeOfficeBuildingIcon,
    AnankePersonIcon,
    AnankeTagIcon,
} from "~/domainModels/workspaceSpecific/icons";
import {
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
    EmotoradEventTypes,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

export function buildEmotradTestrideEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.testride as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    const toSectionColumns = (
        rows: Array<
            | {label: string; isEditable: boolean; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "Auto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {
                  label: string;
                  isEditable: boolean;
                  columnType: "OttopilotEvent";
                  isAdditionalColumn: false;
                  columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              }
        >,
    ) => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = row.columnId ?? getEventColumnId(row.displayNames);
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = row.columnId ?? getEventColumnId(row.displayNames);
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    isEditable: row.isEditable,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`emotorad-testride-edit-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number"],
                                },
                                {
                                    label: "Pincode",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {label: "City", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City"]},
                                {label: "State", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State"]},
                                {
                                    label: "Height of Rider",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider"],
                                },
                                {
                                    label: "Purchase Timeline",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline"],
                                },
                                {
                                    label: "Action platform",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Platform"],
                                },
                                {
                                    label: "Test Ride Date",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Date", "Test ride Date"],
                                },
                                {
                                    label: "Test ride Time",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Time", "Test ride Time"],
                                },
                                {
                                    label: "Last test Ride Date",
                                    isEditable: false,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Test Ride Date", "Last test Ride Date"],
                                },
                                {
                                    label: "Test ride Count",
                                    isEditable: false,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Count", "Test ride Count"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-edit-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model Variant",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient"],
                                },
                                {
                                    label: "Primary Riding purpose",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {label: "Exploring for", isEditable: true, columnType: "Auto", isAdditionalColumn: true, displayNames: ["Exploring for"]},
                                {
                                    label: "Riding Frequency",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-edit-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle owner",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner"],
                                },
                                {label: "Assigned Dealer", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Assigned Dealer"]},
                                {
                                    label: "Assigned Dealer Contact",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact Number", "Assigned Dealer Contact"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email"],
                                },
                                {
                                    label: "Regional Manager",
                                    isEditable: false,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["ZSM Name", "ZSM", "Regional Manager"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Logged on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Logged by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdBy"},
                                {
                                    label: "Scheduled on",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Scheduled by",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {label: "Modified on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {
                                    label: "Cancelled on",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancelled by",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm Medium",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradLeadEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.lead as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    const toSectionColumns = (
        rows: Array<
            | {label: string; isEditable: boolean; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "Auto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {
                  label: string;
                  isEditable: boolean;
                  columnType: "OttopilotEvent";
                  isAdditionalColumn: false;
                  columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              }
        >,
    ) => {
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: columnId,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: journeyColumnId,
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = row.columnId ?? getEventColumnId(row.displayNames);
                    if (eventColumnId !== null) {
                        return {
                            id: eventColumnId,
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = row.columnId ?? getEventColumnId(row.displayNames);
                if (eventColumnId === null) return null;
                return {
                    id: eventColumnId,
                    isEditable: row.isEditable,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`emotorad-lead-edit-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number"],
                                },
                                {label: "Gender", isEditable: true, columnType: "Auto", isAdditionalColumn: true, displayNames: ["Gender"]},
                                {
                                    label: "Pincode",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {
                                    label: "House Number",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "House Number",
                                        "House number",
                                        "House No",
                                        "House no",
                                        "House No.",
                                        "House no.",
                                        "Customer House No",
                                        "Customer House No.",
                                        "Customer House Number",
                                    ],
                                },
                                {label: "City", isEditable: true, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City"]},
                                {label: "State", isEditable: true, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State"]},
                                {
                                    label: "Landmark",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Landmark", "Customer Landmark"],
                                },
                                {
                                    label: "Source Channels",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Source Channels", "Source Channel"],
                                },
                                {
                                    label: "Purchase Timeline",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline"],
                                },
                                {
                                    label: "Height of Rider",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-edit-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Model", isEditable: true, columnType: "Auto", isAdditionalColumn: true, displayNames: ["Model"]},
                                {
                                    label: "Model Varient",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient"],
                                },
                                {
                                    label: "Preferred Colour",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Preferred Colour", "Preferred Color", "Preferred colour"],
                                },
                                {
                                    label: "Is this your first cycle",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Is this your first cycle",
                                        "Is this your first cyle",
                                        "Is this your first Cycle",
                                        "Is this your first Cycle?",
                                        "First Cycle",
                                    ],
                                },
                                {
                                    label: "Primary Riding purpose",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {
                                    label: "Current vehicle",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Current vehicle", "Current Vehicle"],
                                },
                                {
                                    label: "Other Brands Considered",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Other Brands Considered"],
                                },
                                {label: "Exploring for", isEditable: true, columnType: "Auto", isAdditionalColumn: true, displayNames: ["Exploring for"]},
                                {
                                    label: "Riding Frequency",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-edit-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle owner",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner"],
                                },
                                {label: "Assigned Dealer", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Assigned Dealer"]},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Created by (Role)",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Role)", "Created By Role", "Created by role", "Created by Role"],
                                },
                                {label: "Created by name", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdBy"},
                                {label: "Created on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Modified on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm Medium",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradWalkinEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.walkin as Uuid)) {
        return eventTemplate;
    }
    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const toSectionColumns = (
        rows: Array<
            | {label: string; isEditable: boolean; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; isEditable: boolean; columnType: "Auto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {
                  label: string;
                  isEditable: boolean;
                  columnType: "OttopilotEvent";
                  isAdditionalColumn: false;
                  columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              }
        >,
    ) => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    isEditable: row.isEditable,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };
    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`emotorad-walkin-edit-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number"],
                                },
                                {
                                    label: "Pincode",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {label: "City", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City"]},
                                {label: "State", isEditable: false, columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State"]},
                                {label: "Purchase Timeline", isEditable: true, columnType: "Auto", isAdditionalColumn: true, displayNames: ["Purchase Timeline"]},
                                {
                                    label: "Height of Rider",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider"],
                                },
                                {
                                    label: "Action Platform",
                                    isEditable: false,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Current Platform", "Platform"],
                                },
                                {
                                    label: "Last v.l.h date",
                                    isEditable: false,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Last Walk-in Date",
                                        "Last walk-in date",
                                        "Last Walkin Date",
                                        "Last walkin date",
                                        "Last VLH Date",
                                        "Last v.l.h date",
                                    ],
                                },
                                {
                                    label: "With i Cyle?",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Walk-in Count", "Walk in Count", "Walkin Count", "Walkin count", "With i Cyle?", "With i Cycle?"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-edit-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model variant",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient"],
                                },
                                {
                                    label: "Test ride taken ?",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Test Ride Taken",
                                        "Test ride taken",
                                        "Test ride taken ?",
                                        "Test Ride Taken?",
                                        "test ride taken?",
                                        "Test ride Taken",
                                        "Is test ride taken",
                                        "Testride taken",
                                    ],
                                },
                                {
                                    label: "Primary riding purpose",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {
                                    label: "Exploring for",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Exploring for", "Exploring For"],
                                },
                                {
                                    label: "Riding Frequency",
                                    isEditable: true,
                                    columnType: "Auto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency", "Expected riding frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-edit-dispositions`,
                            displayName: "Dispositions",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "L1 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                                {
                                    label: "Remarks",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Disposition Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-edit-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle owner",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner"],
                                },
                                {label: "Assigned Dealer", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: true, displayNames: ["Assigned Dealer"]},
                                {label: "Created on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Modified on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {
                                    label: "Assigned Dealer Contact",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact Number", "Assigned Dealer Contact"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email"],
                                },
                                {label: "Modified by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Logged on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Logged by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdBy"},
                                {
                                    label: "Scheduled on",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Scheduled by",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {label: "Modified on", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {
                                    label: "Cancelled on",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancelled by",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason", "Cancelation Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm Medium",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

type EmotoradCallEditRow =
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEventContainer";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "Auto";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: false;
          columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
      };

export function buildEmotradCallEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.call as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    const toSectionColumns = (rows: Array<EmotoradCallEditRow>) => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                const isRequired = row.isRequired === true;
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-call-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        isRequired,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        isEditable: row.isEditable,
                        isRequired,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            isEditable: row.isEditable,
                            isRequired,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            isEditable: row.isEditable,
                            isRequired,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    isEditable: row.isEditable,
                    isRequired,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`emotorad-call-edit-call-status`,
                            displayName: "Call status",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Call Status",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Status", "Status"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name", "Account"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer Pincode",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {
                                    label: "Customer State",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                                {
                                    label: "Action Platform",
                                    isEditable: false,
                                    columnType: "Auto",
                                    columnId: EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Current Platform", "Platform"],
                                },
                                {
                                    label: "Test Ride Taken",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Taken", "Test ride taken", "Test ride taken ?", "Test Ride Taken?"],
                                },
                                {
                                    label: "Purchase Timeline",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline", "Purchase timeline"],
                                },
                                {
                                    label: "Exploring For",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Exploring for", "Exploring For"],
                                },
                                {
                                    label: "Primary Riding Purpose",
                                    isEditable: true,
                                    columnType: "Auto",
                                    columnId: EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {
                                    label: "Height of Rider",
                                    isEditable: true,
                                    columnType: "Auto",
                                    columnId: EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider"],
                                },
                                {
                                    label: "Expected Riding Frequency",
                                    isEditable: true,
                                    columnType: "Auto",
                                    columnId: EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency", "Expected riding frequency"],
                                },
                                {
                                    label: "Remarks",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Call Remarks", "Logged Call Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model Variant",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-dispositions`,
                            displayName: "Dispositions",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "L1 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Last Call Date",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Call Date", "Last call date"],
                                },
                                {
                                    label: "Call Count",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Count", "Calls Count", "call count"],
                                },
                                {
                                    label: "Scheduled By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {
                                    label: "Scheduled On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Logged By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Logged On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Cancelled By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancelled On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {label: "Modified By", isEditable: false, isRequired: true, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", isEditable: false, isRequired: true, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle Owner",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner"],
                                },
                                {
                                    label: "Assigned Dealer",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer", "Dealer Name"],
                                },
                                {
                                    label: "Assigned Dealer Contact",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact", "Assigned Dealer Contact Number"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email"],
                                },
                                {
                                    label: "KAM Name",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["KAM Name", "KAM"],
                                },
                                {
                                    label: "RM Name",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["RM Name", "ZSM Name", "ZSM", "Regional Manager"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm_platform",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                                },
                                {
                                    label: "Utm_source",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm_medium",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm_campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm_term",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm_content",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

type EmotoradSalesEditRow =
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEventContainer";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "Auto";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          isRequired?: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: false;
          columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
      };

export function buildEmotradSalesEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.sales as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    const toSectionColumns = (rows: Array<EmotoradSalesEditRow>) => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                const isRequired = row.isRequired === true;
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-sales-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        isRequired,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        isEditable: row.isEditable,
                        isRequired,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            isEditable: row.isEditable,
                            isRequired,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            isEditable: row.isEditable,
                            isRequired,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    isEditable: row.isEditable,
                    isRequired,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`emotorad-sales-edit-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name", "Account"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer Pincode",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {
                                    label: "Sale Status",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Sale Status", "Status", "Sales Status"],
                                },
                                {
                                    label: "Source Channel",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Source Channel", "source channel"],
                                },
                                {
                                    label: "Customer State",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                                {
                                    label: "Customer Zone",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Zone", "Zone"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-edit-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model"],
                                },
                                {
                                    label: "Frame Number",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Frame Number", "frame number", "Frame no", "Chassis"],
                                },
                                {
                                    label: "Model Variant",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model variant"],
                                },
                                {
                                    label: "Preferred Color",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Preferred Color", "preferred color", "Color"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Action Platform",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Current Platform", "Platform"],
                                },
                                {
                                    label: "Verified On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Verified On", "verified on", "Closed On", "closed on"],
                                },
                                {
                                    label: "Created by (Role)",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Role)", "Created By Role", "Created by role"],
                                },
                                {
                                    label: "Created by (Name)",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Name)", "Created By Name", "Created by name"],
                                },
                                {label: "Created On", isEditable: false, isRequired: true, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Modified By", isEditable: false, isRequired: true, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", isEditable: false, isRequired: true, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-edit-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Assigned Dealer Contact",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact", "Showroom Contact", "Assigned Dealer Contact Number", "Dealer Contact"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email", "Showroom Mail", "Showroom Email", "Dealer Email"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm_platform",
                                    isEditable: false,
                                    isRequired: true,
                                    columnType: "Auto",
                                    columnId: EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Origin Platform"],
                                },
                                {
                                    label: "Utm_source",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm_medium",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm_campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm_term",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm_content",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}
